import { mediaQueryLarge, isMobileBreakpoint, prefersReducedMotion } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
	static #exclusiveGroupId = 0;

	/** @type {HTMLDetailsElement} */
	get details() {
		const details = this.querySelector('details');

		if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

		return details;
	}

	/** @type {HTMLElement} */
	get summary() {
		const summary = this.details.querySelector('summary');

		if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

		return summary;
	}

	get #disableOnMobile() {
		return this.dataset.disableOnMobile === 'true';
	}

	get #disableOnDesktop() {
		return this.dataset.disableOnDesktop === 'true';
	}

	get #closeWithEscape() {
		return this.dataset.closeWithEscape === 'true';
	}

	get #exclusiveAccordion() {
		const accordion = this.closest('[data-exclusive="true"]');

		return accordion instanceof HTMLElement ? accordion : null;
	}

	#controller = new AbortController();

	connectedCallback() {
		const { signal } = this.#controller;

		this.#configureExclusiveGroup();
		this.#setDefaultOpenState();
		this.#closeOtherExclusiveItems();

		this.addEventListener('keydown', this.#handleKeyDown, { signal });
		this.summary.addEventListener('click', this.handleClick, { signal });
		this.details.addEventListener('toggle', this.#handleToggle, { signal });
		mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
	}

	/**
	 * Handles the disconnect event.
	 */
	disconnectedCallback() {
		// Disconnect all the event listeners
		this.#controller.abort();
	}

	/**
	 * Handles the click event.
	 * @param {Event} event - The event.
	 */
	handleClick = (event) => {
		const isMobile = isMobileBreakpoint();
		const isDesktop = !isMobile;

		// Stop default behaviour from the browser
		if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
			event.preventDefault();
			return;
		}
	};

	/** @type {Animation | null} */
	#closeAnimation = null;

	#configureExclusiveGroup() {
		const accordion = this.#exclusiveAccordion;
		if (!accordion) return;

		if (!accordion.dataset.exclusiveGroup) {
			AccordionCustom.#exclusiveGroupId += 1;
			accordion.dataset.exclusiveGroup = `accordion-exclusive-${AccordionCustom.#exclusiveGroupId}`;
		}

		this.details.setAttribute('name', accordion.dataset.exclusiveGroup);
	}

	#closeOtherExclusiveItems({ animate = false } = {}) {
		if (!this.details.open) return;

		const accordion = this.#exclusiveAccordion;
		if (!accordion) return;

		accordion.querySelectorAll('accordion-custom').forEach((item) => {
			if (!(item instanceof AccordionCustom) || item === this || !item.details.open) return;

			item.#closeAnimation?.cancel();

			if (animate && !prefersReducedMotion()) {
				item.#animateClose(item);
				return;
			}

			item.details.open = false;
		});
	}

	/**
	 * Handles the toggle event on the details element.
	 * If the accordion wrapper has data-exclusive="true", closes all sibling accordion-custom elements.
	 */
	#handleToggle = () => {
		this.#closeOtherExclusiveItems({ animate: true });
	};

	/**
	 * Animates a sibling accordion item closed before removing the open attribute.
	 * @param {AccordionCustom} item
	 */
	#animateClose(item) {
		const content = item.querySelector('.details-content');
		if (!content || !item.details.open) return;

		// Cancel any in-progress close animation on that item
		item.#closeAnimation?.cancel();

		const height = content.offsetHeight;

		item.#closeAnimation = content.animate(
			[
				{ height: `${height}px`, overflow: 'hidden' },
				{ height: '0px', overflow: 'hidden' },
			],
			{ duration: 360, easing: 'ease-out' }
		);

		item.#closeAnimation.onfinish = () => {
			item.details.open = false;
			item.#closeAnimation = null;
		};

		item.#closeAnimation.oncancel = () => {
			item.#closeAnimation = null;
		};
	}

	/**
	 * Handles the media query change event.
	 */
	#handleMediaQueryChange = () => {
		this.#setDefaultOpenState();
	};

	/**
	 * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
	 */
	#setDefaultOpenState() {
		const isMobile = isMobileBreakpoint();

		this.details.open =
			(isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
			(!isMobile && this.hasAttribute('open-by-default-on-desktop'));
	}

	/**
	 * Handles keydown events for the accordion
	 *
	 * @param {KeyboardEvent} event - The keyboard event.
	 */
	#handleKeyDown(event) {
		// Close the accordion when used as a menu
		if (event.key === 'Escape' && this.#closeWithEscape) {
			event.preventDefault();

			this.details.open = false;
			this.summary.focus();
		}
	}
}

if (!customElements.get('accordion-custom')) {
	customElements.define('accordion-custom', AccordionCustom);
}
